import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Camera, Settings, Trophy, Save, Users, LogIn, LogOut, Flame } from 'lucide-react'
import { useTheme } from '../lib/theme-provider'
import { ThemedBackground } from '../components/ThemedBackground'
import { saveToStorage, loadFromStorage, STORAGE_KEYS, saveUserProfile } from '../utils/storage'
import { ThemeSelectorModal } from '../components/ThemeSelectorModal'
import NicknameLoginModal from '../components/NicknameLoginModal'
import ConfirmDialog from '../components/ConfirmDialog'
import { waitForSupabaseAuthReady } from '../utils/supabase'
import { statsDataService } from '../modules/stats/dataService'
import { supabase } from '../lib/supabase'
import { auth } from '../lib/auth'

interface UserProfile {
  nickname: string
  avatar: string
}

interface PlayerStats {
  totalGames: number
  wins: number
  losses: number
  winRate: number
  totalScore: number
  averageScore: number
  rank: number
  goodWins: number
  goodLosses: number
  evilWins: number
  evilLosses: number
  goodGames: number
  evilGames: number
  mvpCount: number
  goodMvpCount: number
  evilMvpCount: number
}

const AVATAR_OPTIONS = [
  '👤', '🧑', '👨', '👩', '🧔', '👱', '🧓', '👴',
  '👵', '🧕', '👮', '👷', '💂', '🕵️', '🏃', '🧘',
]

export default function ProfilePage() {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const [showThemeSelector, setShowThemeSelector] = useState(false)
  const [showAvatarSelector, setShowAvatarSelector] = useState(false)
  const [profile, setProfile] = useState<UserProfile>({
    nickname: '',
    avatar: '👤',
  })
  const [isEditing, setIsEditing] = useState(false)
  const [editedNickname, setEditedNickname] = useState('')
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [stats, setStats] = useState<PlayerStats | null>(null)
  const [playerDetailData, setPlayerDetailData] = useState<any>(null)
  const [isLoadingPlayerDetail, setIsLoadingPlayerDetail] = useState(false)
  const avatarInputRef = useRef<HTMLInputElement>(null)

  // 监听登录状态变化
  useEffect(() => {
    const unsubscribe = auth.subscribe((user) => {
      if (user) {
        setProfile({ nickname: user.nickname, avatar: user.avatar })
        setEditedNickname(user.nickname)
      } else {
        setProfile({ nickname: '', avatar: '' })
        setEditedNickname('')
      }
    })
    
    return unsubscribe
  }, [])

  // Supabase 存储配置
  const bucketName = 'Tenant-dinga3c67f044ba91612a39a90f97fcb1e09'

  // V341: 角色名称映射
  const getRoleName = (role: string): string => {
    const roleMap: Record<string, string> = {
      'merlin': '梅林',
      'percival': '派西维尔',
      'morgana': '莫甘娜',
      'assassin': '刺客',
      'mordred': '莫德雷德',
      'oberon': '奥伯伦',
      'loyal': '忠臣',
      'minion': '爪牙',
      'lancelot_evil': '邪恶兰斯洛特',
      'lancelot_good': '善良兰斯洛特'
    }
    return roleMap[role] || role
  }

  // 加载用户配置（从 auth 模块同步）
  useEffect(() => {
    const user = auth.getCurrentUser()
    if (user) {
      setProfile({ nickname: user.nickname, avatar: user.avatar })
      setEditedNickname(user.nickname)
    } else {
      const savedProfile = loadFromStorage<UserProfile>(STORAGE_KEYS.USER_PROFILE, 365 * 24)
      if (savedProfile) {
        setProfile(savedProfile)
        setEditedNickname(savedProfile.nickname)
      }
    }
  }, [])

  // 加载玩家战绩数据
  useEffect(() => {
    const loadStats = async () => {
      if (!profile.nickname) {
        setStats(null)
        return
      }

      try {
        // 等待 Supabase 认证就绪
        await waitForSupabaseAuthReady()

        console.log('[ProfilePage] 开始查询战绩，昵称:', profile.nickname)

        // V337: 从 game_records + player_round_records 实时计算该昵称的统计数据
        // 与战绩统计功能的取数逻辑一致
        const allGamesResult = await statsDataService.loadHistoryGames('all', 'all')
        const allGamesData = allGamesResult.data
        
        // 根据昵称过滤出该玩家的所有记录
        const playerGames = allGamesData.filter(game => 
          game.players?.some(p => p.player_nickname === profile.nickname)
        )
        
        console.log('[ProfilePage] 找到玩家游戏记录数:', playerGames.length)

        if (playerGames.length === 0) {
          console.log('[ProfilePage] 未找到战绩数据，使用默认值')
          setStats({
            totalGames: 0,
            wins: 0,
            losses: 0,
            winRate: 0,
            totalScore: 0,
            averageScore: 0,
            rank: 0,
            goodWins: 0,
            goodLosses: 0,
            evilWins: 0,
            evilLosses: 0,
            goodGames: 0,
            evilGames: 0,
            mvpCount: 0,
            goodMvpCount: 0,
            evilMvpCount: 0,
          })
          return
        }

        // V337: 使用与 PlayerDetail 相同的计算逻辑
        const playerRecordsMap = new Map<string, any>()
        playerGames.forEach(game => {
          const player = game.players?.find(p => p.player_nickname === profile.nickname)
          if (player) {
            const uniqueKey = `${player.player_nickname}_${player.game_record_id || game.id}`
            if (!playerRecordsMap.has(uniqueKey)) {
              playerRecordsMap.set(uniqueKey, player)
            }
          }
        })
        
        const playerRecords = Array.from(playerRecordsMap.values())
        
        // 统计各项数据
        let wins = 0
        let losses = 0
        let goodWins = 0
        let goodLosses = 0
        let evilWins = 0
        let evilLosses = 0
        let mvpCount = 0
        let totalScore = 0
        
        playerRecords.forEach(record => {
          const isWin = (record.score_change || 0) > 0
          if (isWin) {
            wins++
            if (record.faction === 'good') goodWins++
            if (record.faction === 'evil') evilWins++
          } else {
            losses++
            if (record.faction === 'good') goodLosses++
            if (record.faction === 'evil') evilLosses++
          }
          if (record.is_mvp) mvpCount++
          totalScore += (record.score_change || 0)
        })
        
        const totalGames = playerRecords.length
        const winRate = totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0
        const averageScore = totalGames > 0 ? Math.round((totalScore / totalGames) * 10) / 10 : 0

        console.log('[ProfilePage] 计算得到的战绩数据:', {
          totalGames,
          wins,
          losses,
          winRate,
          totalScore,
          averageScore,
          goodWins,
          goodLosses,
          evilWins,
          evilLosses,
          mvpCount,
        })

        setStats({
          totalGames,
          wins,
          losses,
          winRate,
          totalScore: Math.round(totalScore),
          averageScore,
          rank: 0,
          goodWins,
          goodLosses,
          evilWins,
          evilLosses,
          goodGames: goodWins + goodLosses,
          evilGames: evilWins + evilLosses,
          mvpCount,
          goodMvpCount: 0,
          evilMvpCount: 0,
        })

        // 预加载个人详情页数据：按角色统计和对阵分析
        const calculatePlayerDetailData = async () => {
          setIsLoadingPlayerDetail(true)
          try {
            // 角色统计
            const roleStats: Record<string, { games: number; wins: number; losses: number; mvpCount: number }> = {}
            playerRecords.forEach(record => {
              const roleName = record.player_role || '未知'
              if (!roleStats[roleName]) {
                roleStats[roleName] = { games: 0, wins: 0, losses: 0, mvpCount: 0 }
              }
              const isWin = (record.score_change || 0) > 0
              roleStats[roleName].games++
              if (isWin) roleStats[roleName].wins++
              else roleStats[roleName].losses++
              if (record.is_mvp) roleStats[roleName].mvpCount++
            })

            // V341: 计算角色抓取率（出场次数/总场次）
            const rolePickRates = Object.entries(roleStats).map(([roleName, stats]) => ({
              roleName,
              pickRate: totalGames > 0 ? Math.round((stats.games / totalGames) * 100) : 0,
            })).sort((a, b) => b.pickRate - a.pickRate)

            // 对阵分析 - 统计与其他玩家的对战记录
            const vsPlayersMap = new Map<string, { games: number; wins: number; losses: number }>()
            playerGames.forEach(game => {
              const currentPlayer = game.players?.find(p => p.player_nickname === profile.nickname)
              if (!currentPlayer) return

              const isWin = (currentPlayer.score_change || 0) > 0
              
              // 找出同场的其他玩家
              game.players?.forEach(otherPlayer => {
                if (otherPlayer.player_nickname !== profile.nickname && otherPlayer.player_nickname) {
                  const key = otherPlayer.player_nickname
                  if (!vsPlayersMap.has(key)) {
                    vsPlayersMap.set(key, { games: 0, wins: 0, losses: 0 })
                  }
                  const stat = vsPlayersMap.get(key)!
                  stat.games++
                  if (isWin) stat.wins++
                  else stat.losses++
                }
              })
            })

            const vsPlayers = Array.from(vsPlayersMap.entries())
              .map(([nickname, stats]) => ({
                nickname,
                ...stats,
                winRate: stats.games > 0 ? Math.round((stats.wins / stats.games) * 100) : 0,
              }))
              .sort((a, b) => b.games - a.games)
              .slice(0, 20) // 只显示前 20 个对手

            // V372: 计算连胜连败状态 - 使用与 WinStreakTracker 相同的连续链逻辑
            let streakInfo = null
            if (playerRecords.length > 0) {
              // 构建游戏记录数组（包含日期和局数信息）
              const gameHistory: { 
                game_date: string
                game_number: number
                score_change: number
              }[] = []
              
              playerRecords.forEach(record => {
                // 从 allGames 中查找对应的游戏记录获取日期和局数
                const game = playerGames.find(g => 
                  g.players?.some(p => p.player_nickname === profile.nickname && p.game_record_id === (record.game_record_id || record.id))
                )
                if (game) {
                  gameHistory.push({
                    game_date: game.game_date || '',
                    game_number: game.game_number || 0,
                    score_change: record.score_change || 0,
                  })
                }
              })
              
              // 按日期 + 局数排序
              const sortedGames = gameHistory.sort((a, b) => {
                const dateA = new Date(a.game_date).getTime()
                const dateB = new Date(b.game_date).getTime()
                if (dateA !== dateB) return dateA - dateB
                return a.game_number - b.game_number
              })
              
              // 构建连续关系链
              const consecutiveChains: Array<{ games: typeof sortedGames }> = []
              let currentChain: typeof sortedGames = []
              
              for (let i = 0; i < sortedGames.length; i++) {
                const current = sortedGames[i]
                const next = sortedGames[i + 1]
                
                currentChain.push(current)
                
                if (next) {
                  const dateA = new Date(current.game_date)
                  const dateB = new Date(next.game_date)
                  const isConsecutive = (() => {
                    // 同一天，局数连续
                    if (dateA.toDateString() === dateB.toDateString()) {
                      return next.game_number === current.game_number + 1
                    }
                    // 跨天连续
                    const gamesOnDateA = sortedGames.filter(g => 
                      new Date(g.game_date).toDateString() === dateA.toDateString()
                    )
                    const isLastGameOfDateA = current === gamesOnDateA[gamesOnDateA.length - 1]
                    
                    const gamesAfterDateA = sortedGames.filter(g => 
                      new Date(g.game_date) > dateA
                    ).sort((x, y) => 
                      new Date(x.game_date).getTime() - new Date(y.game_date).getTime()
                    )
                    
                    const isFirstGameAfterDateA = next === gamesAfterDateA[0]
                    
                    return isLastGameOfDateA && isFirstGameAfterDateA
                  })()
                  
                  if (!isConsecutive) {
                    consecutiveChains.push({ games: [...currentChain] })
                    currentChain = []
                  }
                } else {
                  consecutiveChains.push({ games: [...currentChain] })
                }
              }
              
              // 从最后一个连续链计算当前连胜/连败
              if (consecutiveChains.length > 0) {
                const lastChain = consecutiveChains[consecutiveChains.length - 1]
                if (lastChain && lastChain.games.length > 0) {
                  const lastGame = lastChain.games[lastChain.games.length - 1]
                  const currentType = (lastGame.score_change || 0) > 0 ? 'win' : 'loss'
                  
                  let currentStreak = 0
                  for (let i = lastChain.games.length - 1; i >= 0; i--) {
                    const game = lastChain.games[i]
                    const isWin = (game.score_change || 0) > 0
                    const result: 'win' | 'loss' = isWin ? 'win' : 'loss'
                    
                    if (result === currentType) {
                      currentStreak++
                    } else {
                      break
                    }
                  }
                  
                  if (currentStreak >= 1) {
                    streakInfo = {
                      type: currentType,
                      count: currentStreak,
                    }
                  }
                }
              }
            }

            setPlayerDetailData({
              roleStats: Object.entries(roleStats).map(([roleName, stats]) => ({
                roleName,
                ...stats,
                winRate: stats.games > 0 ? Math.round((stats.wins / stats.games) * 100) : 0,
              })).sort((a, b) => b.games - a.games),
              rolePickRates, // V341: 角色抓取率数据
              vsPlayers,
              totalGames,
              wins,
              losses,
              winRate,
              totalScore: Math.round(totalScore),
              averageScore,
              goodWins,
              goodLosses,
              evilWins,
              evilLosses,
              mvpCount,
              streakInfo,
            })
            
            console.log('[ProfilePage] 预加载的个人详情数据:', {
              roleStatsCount: Object.keys(roleStats).length,
              vsPlayersCount: vsPlayers.length,
              rolePickRatesCount: rolePickRates.length, // V341: 日志
            })
          } catch (err) {
            console.error('[ProfilePage] 预加载个人详情数据失败:', err)
          } finally {
            setIsLoadingPlayerDetail(false)
          }
        }

        calculatePlayerDetailData()
      } catch (err) {
        console.error('[ProfilePage] 加载战绩数据失败:', err)
        setStats({
          totalGames: 0,
          wins: 0,
          losses: 0,
          winRate: 0,
          totalScore: 0,
          averageScore: 0,
          rank: 0,
          goodWins: 0,
          goodLosses: 0,
          evilWins: 0,
          evilLosses: 0,
          goodGames: 0,
          evilGames: 0,
          mvpCount: 0,
          goodMvpCount: 0,
          evilMvpCount: 0,
        })
      }
    }

    loadStats()
  }, [profile.nickname])



  // 保存用户配置
  const handleSaveProfile = () => {
    if (editedNickname.trim().length < 2 || editedNickname.trim().length > 16) {
      alert('昵称长度为 2-16 个字符')
      return
    }

    const user = auth.getCurrentUser()
    if (user) {
      // 已登录用户，更新 auth 模块
      auth.updateUser({ nickname: editedNickname.trim() })
      // 同步到旧存储（兼容）
      saveUserProfile(editedNickname.trim(), profile.avatar)
    } else {
      // 未登录用户，仅保存到本地
      const newProfile: UserProfile = {
        nickname: editedNickname.trim(),
        avatar: profile.avatar,
      }
      saveToStorage(STORAGE_KEYS.USER_PROFILE, newProfile, 365 * 24)
      saveUserProfile(newProfile.nickname, newProfile.avatar)
      setProfile(newProfile)
    }
    
    setIsEditing(false)
  }

  const handleSelectAvatar = (avatar: string) => {
    const user = auth.getCurrentUser()
    if (user) {
      auth.updateUser({ avatar })
      saveUserProfile(user.nickname, avatar)
    } else {
      const newProfile: UserProfile = {
        nickname: profile.nickname,
        avatar,
      }
      saveToStorage(STORAGE_KEYS.USER_PROFILE, newProfile, 365 * 24)
      setProfile(newProfile)
    }
    setShowAvatarSelector(false)
  }

  const handleAvatarUploadClick = () => {
    avatarInputRef.current?.click()
  }

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      // 读取文件并转换为 ArrayBuffer
      const arrayBuffer = await file.arrayBuffer()
      
      // 上传到 Supabase Storage
      const fileName = `public/avatars/${Date.now()}_${file.name}`
      
      const { error } = await supabase.storage
        .from(bucketName)
        .upload(fileName, arrayBuffer, {
          contentType: file.type,
          upsert: false,
        })

      if (error) throw error

      // 获取公共 URL
      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(fileName)

      // 更新用户资料
      const newAvatarUrl = urlData.publicUrl
      const user = auth.getCurrentUser()
      if (user) {
        auth.updateUser({ avatar: newAvatarUrl })
        saveUserProfile(user.nickname, newAvatarUrl)
      } else {
        const newProfile: UserProfile = {
          nickname: profile.nickname,
          avatar: newAvatarUrl,
        }
        saveToStorage(STORAGE_KEYS.USER_PROFILE, newProfile, 365 * 24)
        setProfile(newProfile)
      }
    } catch (error) {
      console.error('头像上传失败:', error)
      alert('头像上传失败，请重试')
    } finally {
      // 清空 input 以允许重复选择同一文件
      if (avatarInputRef.current) {
        avatarInputRef.current.value = ''
      }
    }
  }

  const hasChanges = editedNickname.trim() !== profile.nickname

  return (
    <ThemedBackground pageName="profile" className="p-4">
      {/* Profile Card */}
      <div className="rounded-2xl p-5 mb-5 relative avalon-card">
        {/* Settings Button - moved to top-right corner of card */}
        <button
          onClick={() => setShowThemeSelector(true)}
          className="absolute top-3 right-3 w-9 h-9 rounded-lg flex items-center justify-center transition-all hover:bg-avalon-purple/20"
        >
          <Settings className="w-4 h-4 text-avalon-gold" />
        </button>
        {/* Avatar & Nickname */}
        <div className="flex flex-col items-center mb-4">
          <div className="relative mb-3">
            {profile.avatar && (profile.avatar.startsWith('http://') || profile.avatar.startsWith('https://')) ? (
              <img 
                src={profile.avatar}
                alt="avatar"
                className="w-20 h-20 rounded-full object-cover cursor-pointer transition-transform hover:scale-105"
                style={{
                  border: `1px solid ${theme.colors['accent-gold']}60`,
                  boxShadow: '0 0 8px rgba(212, 175, 55, 0.2)',
                }}
                onClick={() => setShowAvatarSelector(true)}
              />
            ) : (
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center text-4xl cursor-pointer transition-transform hover:scale-105"
                style={{
                  backgroundColor: theme.colors['bg-secondary'],
                  border: `1px solid ${theme.colors['accent-gold']}60`,
                  boxShadow: '0 0 8px rgba(212, 175, 55, 0.2)',
                }}
                onClick={() => setShowAvatarSelector(true)}
              >
                {profile.avatar}
              </div>
            )}
            <button
              onClick={() => handleAvatarUploadClick()}
              className="absolute bottom-0 right-0 w-7 h-7 rounded-full flex items-center justify-center shadow-lg"
              style={{
                backgroundColor: theme.colors['accent-gold'],
              }}
            >
              <Camera className="w-3.5 h-3.5 text-[#1A0F2A]" />
            </button>
            <input
              type="file"
              ref={avatarInputRef}
              onChange={handleAvatarUpload}
              accept="image/*"
              className="hidden"
            />
          </div>

          {isEditing ? (
            <div className="w-full max-w-xs">
              <input
                type="text"
                value={editedNickname}
                onChange={(e) => setEditedNickname(e.target.value)}
                placeholder="输入昵称"
                className="w-full text-center text-base font-semibold px-3 py-2 rounded-xl mb-3"
                style={{
                  backgroundColor: theme.colors['bg-secondary'],
                  color: theme.colors['text-primary'],
                  border: `2px solid ${theme.colors['border-primary']}`,
                }}
                maxLength={16}
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setIsEditing(false)
                    setEditedNickname(profile.nickname)
                  }}
                  className="flex-1 px-3 py-2 rounded-xl font-medium transition-all text-sm"
                  style={{
                    backgroundColor: theme.colors['bg-secondary'],
                    color: theme.colors['text-secondary'],
                    border: `1px solid ${theme.colors['border-primary']}`,
                  }}
                >
                  取消
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={!hasChanges}
                  className="flex-1 px-3 py-2 rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
                  style={{
                    backgroundColor: hasChanges ? theme.colors['accent-gold'] : theme.colors['bg-secondary'],
                    color: hasChanges ? '#1A0F2A' : theme.colors['text-muted'],
                  }}
                >
                  <Save className="w-3.5 h-3.5" />
                  保存
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 w-full justify-center">
              <h2 
                className="text-lg font-bold"
                style={{ color: theme.colors['text-primary'] }}
              >
                {auth.isLoggedIn() ? (profile.nickname || '未设置昵称') : '未登录'}
              </h2>
              <button
                onClick={() => {
                  if (auth.isLoggedIn()) {
                    setShowLogoutConfirm(true)
                  } else {
                    setShowLoginModal(true)
                  }
                }}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:bg-avalon-purple/20"
                style={{
                  backgroundColor: `${theme.colors['accent-gold']}20`,
                  border: `1px solid ${theme.colors['accent-gold']}40`,
                }}
              >
                {auth.isLoggedIn() ? (
                  <LogOut className="w-4 h-4" style={{ color: theme.colors['accent-gold'] }} />
                ) : (
                  <LogIn className="w-4 h-4" style={{ color: theme.colors['accent-gold'] }} />
                )}
              </button>
            </div>
          )}
        </div>

        {/* Stats Details */}
        <div className="border-t pt-4" style={{ borderColor: `${theme.colors['border-primary']}40` }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-avalon-gold" />
              <h3 className="text-base font-semibold text-white">
                个人战绩
              </h3>
            </div>
            <button
              onClick={() => {
                if (profile.nickname) {
                  // V339: 将预加载的数据通过 session 存储传递给详情页
                  if (playerDetailData) {
                    sessionStorage.setItem('player-detail-cache', JSON.stringify({
                      nickname: profile.nickname,
                      data: playerDetailData,
                      timestamp: Date.now(),
                    }))
                    // V339: 分别缓存角色统计和对阵数据（兼容旧版 PlayerDetail）
                    sessionStorage.setItem('player-detail-role-stats', JSON.stringify(playerDetailData.roleStats))
                    sessionStorage.setItem('player-detail-vs-players', JSON.stringify(playerDetailData.vsPlayers))
                    console.log('[ProfilePage] 已缓存个人详情数据:', {
                      roleStatsCount: playerDetailData.roleStats.length,
                      vsPlayersCount: playerDetailData.vsPlayers.length,
                    })
                  }
                  navigate(`/player-detail?nickname=${encodeURIComponent(profile.nickname)}`)
                }
              }}
              disabled={isLoadingPlayerDetail}
              className="px-3 py-1.5 rounded-xl font-medium transition-all text-xs disabled:opacity-50 bg-avalon-card text-avalon-gold border border-avalon-gold/30 hover:bg-avalon-purple/20"
            >
              {isLoadingPlayerDetail ? '加载中...' : '查看详细战绩'}
            </button>
          </div>
          
          {profile.nickname && stats ? (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="avalon-card p-2 text-center">
                  <p className="text-[12px] mb-0.5 text-avalon-text">胜率</p>
                  <p className="text-lg font-bold text-avalon-gold">{stats?.winRate || 0}%</p>
                </div>
                <div className="avalon-card p-2 text-center">
                  <p className="text-xs mb-0.5 text-avalon-text">总积分</p>
                  <p className="text-lg font-bold text-white">{stats?.totalScore || 0}</p>
                </div>
                <div className="avalon-card p-2 text-center">
                  <p className="text-xs mb-0.5 text-avalon-text">MVP</p>
                  <p className="text-lg font-bold text-avalon-gold">{stats?.mvpCount || 0}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="avalon-card p-2 text-center">
                  <p className="text-xs mb-0.5 text-avalon-text">总场次</p>
                  <p className="text-lg font-bold text-white">{stats?.totalGames || 0}</p>
                </div>
                <div className="avalon-card p-2 text-center">
                  <p className="text-xs mb-0.5 text-avalon-text">胜/负</p>
                  <p className="text-lg font-bold">
                    <span className="text-green-400">{stats?.wins || 0}</span>
                    <span className="text-xs mx-1 text-avalon-text">/</span>
                    <span className="text-red-400">{stats?.losses || 0}</span>
                  </p>
                </div>
              </div>

              {/* 连胜连败状态 */}
              {playerDetailData?.streakInfo && (
                <div className={`mb-3 rounded-xl p-3 border flex items-center gap-3 ${
                  playerDetailData.streakInfo.type === 'win' 
                    ? 'bg-gradient-to-r from-green-900/40 to-emerald-900/40 border-green-500/30'
                    : 'bg-gradient-to-r from-red-900/40 to-rose-900/40 border-red-500/30'
                }`}>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    playerDetailData.streakInfo.type === 'win' 
                      ? 'bg-green-500/20'
                      : 'bg-red-500/20'
                  }`}>
                    {playerDetailData.streakInfo.type === 'win' ? (
                      <Flame className="w-6 h-6 text-green-400" />
                    ) : (
                      <Flame className="w-6 h-6 text-red-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-avalon-text mb-0.5">当前状态</p>
                    <p className={`text-2xl font-bold ${playerDetailData.streakInfo.type === 'win' ? 'text-green-400' : 'text-red-400'}`}>
                      {playerDetailData.streakInfo.count}{playerDetailData.streakInfo.type === 'win' ? '连胜' : '连败'}
                    </p>
                  </div>
                </div>
              )}

              {/* 阵营胜率 */}
              <div className="avalon-card p-2 mb-3">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2 text-base">
                  <Users className="w-3.5 h-3.5 text-avalon-gold" />
                  阵营胜率
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex flex-col items-center p-1.5 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <div className="flex items-center gap-1 mb-0.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                      <span className="text-blue-400 font-medium text-xs">正义阵营</span>
                    </div>
                    <div className="text-center">
                      {(stats?.totalGames || 0) > 0 ? (
                        <span className="text-white font-bold text-sm">
                          {(stats?.goodWins || 0) + (stats?.goodLosses || 0) > 0 
                            ? Math.round(((stats?.goodWins || 0) / ((stats?.goodWins || 0) + (stats?.goodLosses || 0))) * 100) + '%'
                            : '0%'}
                        </span>
                      ) : (
                        <span className="text-avalon-text font-bold text-sm">-</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-center p-1.5 bg-red-500/10 rounded-lg border border-red-500/30">
                    <div className="flex items-center gap-1 mb-0.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                      <span className="text-red-400 font-medium text-xs">邪恶阵营</span>
                    </div>
                    <div className="text-center">
                      {(stats?.totalGames || 0) > 0 ? (
                        <span className="text-white font-bold text-sm">
                          {(stats?.evilWins || 0) + (stats?.evilLosses || 0) > 0 
                            ? Math.round(((stats?.evilWins || 0) / ((stats?.evilWins || 0) + (stats?.evilLosses || 0))) * 100) + '%'
                            : '0%'}
                        </span>
                      ) : (
                        <span className="text-avalon-text font-bold text-sm">-</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* 阵营抓取率 */}
              <div className="avalon-card p-2 mb-3">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2 text-base">
                  <Users className="w-3.5 h-3.5 text-avalon-gold" />
                  阵营抓取率
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex flex-col items-center p-1.5 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <div className="flex items-center gap-1 mb-0.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                      <span className="text-blue-400 font-medium text-xs">正义阵营</span>
                    </div>
                    <div className="text-center">
                      {(stats?.totalGames || 0) > 0 ? (
                        <span className="text-white font-bold text-sm">
                          {Math.round(((stats?.goodGames || 0) / (stats?.totalGames || 1)) * 100)}%
                        </span>
                      ) : (
                        <span className="text-avalon-text font-bold text-sm">-</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-center p-1.5 bg-red-500/10 rounded-lg border border-red-500/30">
                    <div className="flex items-center gap-1 mb-0.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                      <span className="text-red-400 font-medium text-xs">邪恶阵营</span>
                    </div>
                    <div className="text-center">
                      {(stats?.totalGames || 0) > 0 ? (
                        <span className="text-white font-bold text-sm">
                          {Math.round(((stats?.evilGames || 0) / (stats?.totalGames || 1)) * 100)}%
                        </span>
                      ) : (
                        <span className="text-avalon-text font-bold text-sm">-</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* 角色数据明细 */}
              {playerDetailData?.roleStats && playerDetailData.roleStats.length > 0 && (
                <div className="avalon-card overflow-hidden mb-3">
                  <div className="p-2 border-b border-avalon-purple/20">
                    <h3 className="text-white font-semibold flex items-center gap-2 text-base">
                      <Trophy className="w-4 h-4 text-avalon-gold" />
                      角色数据明细
                    </h3>
                  </div>
                  <div className="w-full max-h-48 overflow-y-auto">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-avalon-card">
                        <tr className="border-b border-avalon-purple/20">
                          <th className="text-left py-1.5 px-2 text-avalon-text text-xs font-medium">角色</th>
                          <th className="text-center py-1.5 px-2 text-avalon-text text-xs font-medium">胜/负</th>
                          <th className="text-center py-1.5 px-2 text-avalon-text text-xs font-medium">胜率</th>
                        </tr>
                      </thead>
                      <tbody>
                        {playerDetailData.roleStats.map((item: any, index: number) => (
                          <tr
                            key={index}
                            className={`border-b border-avalon-purple/10 last:border-0 ${
                              index % 2 === 0 ? 'bg-avalon-bg-dark/30' : ''
                            }`}
                          >
                            <td className="py-1.5 px-2 text-white text-xs">{getRoleName(item.roleName)}</td>
                            <td className="py-1.5 px-2 text-center text-avalon-text text-xs">
                              <span className="text-green-400">{item.wins}</span>
                              <span className="text-avalon-text mx-0.5">/</span>
                              <span className="text-red-400">{item.losses}</span>
                            </td>
                            <td className="py-1.5 px-2 text-center">
                              <span className={`text-xs font-medium ${
                                item.games === 0
                                  ? 'text-avalon-text'
                                  : item.winRate >= 60
                                  ? 'text-green-400'
                                  : item.winRate >= 40
                                  ? 'text-avalon-gold'
                                  : 'text-red-400'
                              }`}>
                                {item.games === 0 ? '0%' : `${item.winRate}%`}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-avalon-text">
                设置昵称后开始记录战绩
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Avatar Selector Modal */}
      {showAvatarSelector && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="rounded-2xl p-6 max-w-md w-full max-h-[70vh] overflow-y-auto avalon-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                选择头像
              </h3>
              <button
                onClick={() => setShowAvatarSelector(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-avalon-purple/20 transition-colors"
              >
                <svg className="w-5 h-5 text-avalon-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-4 gap-3">
              {AVATAR_OPTIONS.map((avatar) => (
                <button
                  key={avatar}
                  onClick={() => handleSelectAvatar(avatar)}
                  className={`w-14 h-14 rounded-xl flex items-center justify-center text-3xl transition-all hover:scale-110 avalon-card ${
                    profile.avatar === avatar ? 'ring-2 ring-avalon-gold' : ''
                  }`}
                >
                  {avatar}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Theme Selector Modal */}
      <ThemeSelectorModal 
        isOpen={showThemeSelector} 
        onClose={() => setShowThemeSelector(false)} 
      />
      
      {/* Nickname Login Modal */}
      <NicknameLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={() => {
          // auth module already handles login in the modal
          setShowLoginModal(false)
        }}
      />
      
      {/* Logout Confirm Dialog */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        title="确认退出登录"
        message="确定要退出当前账号吗？"
        confirmText="退出"
        cancelText="取消"
        onConfirm={() => {
          auth.logout()
          setStats(null)
          setPlayerDetailData(null)
          setShowAvatarSelector(false)
          setShowLogoutConfirm(false)
        }}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </ThemedBackground>
  )
}
